import {
  CARD_THEME_COLORS,
  WIDGET_TYPES,
  luminanceOf,
  type WidgetType,
} from '@flexbyt/shared';
import { cx } from '@flexbyt/widget-preview';
import { createFileRoute } from '@tanstack/react-router';
import { WidgetShowcase } from '../components/WidgetShowcase.js';
import { SAMPLE_SETS, fill, sampleSetById } from '../content/sample-sets.js';
import { seo } from '../lib/seo.js';

/**
 * One widget, alone on a page, for framing.
 *
 * ===========================================================================
 * Why this route has to exist
 * ===========================================================================
 * The review layouts size themselves from **viewport** media queries — the
 * renderer's grid is `sm:grid-cols-2 xl:grid-cols-3`, Glide reads
 * `window.innerWidth`. That is correct for a widget dropped into a customer's
 * page, and it makes a side-by-side "phone / tablet / desktop" comparison
 * impossible in the ordinary way: three narrow boxes on one 1400px viewport all
 * render the desktop layout, squeezed, with text overlapping.
 *
 * An iframe has its own viewport. A 375px-wide frame *is* a 375px screen as far
 * as the media queries inside it are concerned, so this route rendered into one
 * shows the real mobile layout rather than a shrunken desktop one.
 *
 * It is also what the free builder will need for its responsive preview, which
 * is why it is a route rather than something folded into the widget page.
 *
 * ===========================================================================
 * Not for people
 * ===========================================================================
 * `noindex`, no navigation, no chrome. Somebody who lands here directly sees a
 * widget on a white page, which is exactly what it is.
 */
export const Route = createFileRoute('/preview/widget')({
  validateSearch: (search: Record<string, unknown>) => {
    const type = String(search.type ?? 'masonry');
    const dark = search.dark === '1' || search.dark === true;

    /*
      A hex colour, or nothing.

      The builder's colour pickers have to reach the framed mobile preview or
      the two halves of that page disagree — the desktop preview repainted and
      the phone still white. They arrive as query parameters, and a query
      parameter is not a thing to trust: this is interpolated straight into a
      `style` attribute by the renderer, so anything that is not six or three
      hex digits is dropped rather than sanitised. A rejected colour falls back
      to the product default, which is a widget that looks slightly wrong; an
      accepted one that is not a colour is a CSS injection.
    */
    const hex = (value: unknown): string | undefined => {
      const raw = typeof value === 'string' ? value.trim() : '';
      return /^#[0-9a-f]{3}$|^#[0-9a-f]{6}$/i.test(raw) ? raw : undefined;
    };

    return {
      // Narrowed against the product's own list: this value picks a renderer,
      // and a query string is not a thing to trust.
      type: ((WIDGET_TYPES as readonly string[]).includes(type)
        ? type
        : 'masonry') as WidgetType,
      count: Math.min(Math.max(Number(search.count ?? 6) || 6, 1), 12),
      /*
        Which sample business the reviews are about. The builder frames this
        route to show a narrow viewport, and a frame showing hotel reviews while
        the page beside it shows a plumber is a frame that demonstrates nothing.
      */
      set: SAMPLE_SETS.some((entry) => entry.id === search.set)
        ? String(search.set)
        : SAMPLE_SETS[0]!.id,
      /*
        Omitted rather than returned as `false`.

        The router rewrites the URL to match whatever this returns, so a
        literal `false` means every frame is loaded with `?dark=false` missing,
        redirected 307 to add it, and loaded again — three extra round-trips per
        widget page, for a default.
      */
      ...(dark ? { dark: true as const } : {}),

      /*
        Same omit-when-default treatment as `dark`, and for the same reason —
        every key returned here is written back into the URL, so returning
        `undefined` keys would add `?card=&text=` to every frame.
      */
      ...(hex(search.card) ? { card: hex(search.card)! } : {}),
      ...(hex(search.text) ? { text: hex(search.text)! } : {}),
      ...(hex(search.border) ? { border: hex(search.border)! } : {}),
      ...(hex(search.star) ? { star: hex(search.star)! } : {}),
      ...(search.noborder === '1' || search.noborder === true
        ? { noborder: true as const }
        : {}),
      ...(search.radius !== undefined && Number.isFinite(Number(search.radius))
        ? { radius: Math.min(Math.max(Number(search.radius), 0), 24) }
        : {}),
    };
  },
  head: () =>
    seo({
      title: 'Widget preview',
      description: 'A single ReviewsJet widget, for framing.',
      path: '/preview/widget',
      noIndex: true,
    }),
  component: FramedWidget,
});

function FramedWidget() {
  const {
    type,
    count,
    dark = false,
    set,
    card,
    text,
    border,
    star,
    noborder,
    radius,
  } = Route.useSearch();

  /*
    Only the overrides, never a full settings object.

    This used to spread `DEFAULT_WIDGET_SETTINGS` and hand the result to
    `WidgetShowcase`. That looks harmless and quietly undid one of the
    showcase's own defaults: it applies `removeBranding: true` *before* the
    caller's settings, so a caller passing the complete defaults — including
    `removeBranding: false` — turned the branding line back on. Every widget
    reached through this route is inside an iframe on a ReviewsJet page, so
    "Powered by ReviewsJet" reappeared there and nowhere else, which is a
    confusing thing to find.

    Passing a partial keeps the escape hatch meaningful: a caller that says
    `removeBranding: false` now means it.
  */
  /*
    The theme first, then anything the visitor changed on top of it — the same
    order the builder applies them in, so the framed phone and the inline
    desktop cannot show different widgets. See `CARD_THEME_COLORS` in the shared
    package for why dark is a preset rather than a mode.
  */
  const settings = {
    ...(dark ? CARD_THEME_COLORS.dark : {}),
    ...(card ? { reviewCardColor: card } : {}),
    ...(text ? { textColor: text } : {}),
    ...(border ? { borderColor: border } : {}),
    ...(star ? { starColor: star } : {}),
    ...(noborder ? { showBorder: false } : {}),
    ...(radius !== undefined ? { borderRadius: radius } : {}),
  };

  /*
    This page's own background, not our preview canvas — the iframe is standing
    in for "the customer's own site", and a real page has a plain background
    rather than a dotted texture. It follows the *card's* luminance rather than
    the `dark` flag alone, because the builder now lets somebody paint the card
    any colour: pick a near-black that is not the preset and `dark` is false,
    but the marquee's edge mask still fades into whatever is behind it, and a
    dark card on a white iframe page produces the same white-smear mismatch the
    builder's own canvas exists to avoid.
  */
  const effectiveCardColor = settings.reviewCardColor ?? CARD_THEME_COLORS.light.reviewCardColor;

  return (
    <div
      className={cx(
        'p-3',
        luminanceOf(effectiveCardColor) <= 0.5 && 'bg-ink-950',
        /*
          The spotlight is a card in a corner *of the page*, and in this frame
          the page is the whole frame. Its stage is sized for the full-window
          editor — `clamp(20rem, 100dvh - 28rem, 36rem)` — which in a 620px
          frame bottoms out at 20rem, filling the top half and leaving the
          "bottom-left" card floating mid-screen on the phone and tablet views.
          Important because that rule is unlayered and utilities are not: an
          unlayered declaration beats a layered one whatever the specificity.
        */
        type === 'spotlight' && 'h-dvh [&_.rj-spotlight-stage]:h-full!'
      )}
    >
      <WidgetShowcase
        type={type}
        reviews={fill(sampleSetById(set).reviews, count)}
        settings={settings}
      />
    </div>
  );
}
